import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Package, ArrowUpRight, Heart } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import { identify as swsIdentify, trackConversion } from "@/lib/analyticsClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MerchSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [linkedSignup, setLinkedSignup] = useState(false);
  const attemptsRef = useRef(0);
  const conversionFiredRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/merch/order/${sessionId}`);
        const d = await r.json();
        if (!alive) return;
        setOrder(d);
        attemptsRef.current += 1;
        // Fire the merch_purchase conversion exactly once — as soon as the
        // order shows as paid. Idempotent via conversionFiredRef.
        if (d?.payment_status === "paid" && !conversionFiredRef.current) {
          conversionFiredRef.current = true;
          const email = (d?.recipient?.email || d?.email || "").trim();
          try {
            if (email) swsIdentify(email, "merch_purchase", d?.recipient?.name || "");
            trackConversion("merch_purchase", {
              email,
              value: Number(d?.total || 0),
              refId: d?.id || d?.printful_order_id || sessionId,
            });
          } catch { /* silent */ }
        }
        // If this order came from the Sacrifice signup form, the backend has already
        // auto-confirmed the tribute-wall entry — mark linked + clear the pending flags.
        if (d?.wall_entry_id || d?.signup_id) {
          setLinkedSignup(true);
          try {
            window.localStorage.removeItem("sws_pending_signup");
            window.localStorage.removeItem("sws_pending_signup_email");
          } catch (err) { console.warn("MerchSuccess: localStorage cleanup failed:", err); }
        }
        if (!d?.printful_order_id && attemptsRef.current < 15) {
          timer = setTimeout(poll, 2000);
        }
      } catch (err) {
        console.warn("MerchSuccess: order poll failed, will retry:", err);
        if (attemptsRef.current < 10) timer = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const paid = order?.payment_status === "paid";
  const printfulLive = !!order?.printful_order_id;
  const shipped = !!order?.tracking?.tracking_number;
  const buyerEmail = order?.recipient?.email || order?.email || "";
  const buyerName = order?.recipient?.name || "";
  const signupHref = `/sweat-for-sacrifice?${new URLSearchParams({
    ...(buyerEmail ? { email: buyerEmail } : {}),
    ...(buyerName ? { name: buyerName } : {}),
    src: "merch",
  }).toString()}#signup`;

  return (
    <main data-testid="merch-success-page" className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-6 p-6 pt-24 pb-16 font-body">
      <Nav />
      <div className="max-w-xl w-full border-2 border-[#B32027] bg-[#0F0F0F] p-8 md:p-12 text-center space-y-6">
        <CheckCircle size={64} weight="fill" className="text-[#B32027] mx-auto" />
        <div>
          <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-3">/ Order confirmed</p>
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter leading-[0.9]">
            You wear<br />the tribute.
          </h1>
        </div>
        <div className="border-t border-white/10 pt-6 space-y-3 text-left">
          <Row label="Payment" value={paid ? "Confirmed ✓" : "Confirming…"} good={paid} />
          <Row label="Order number" value={printfulLive ? `#${order.printful_order_id}` : "Preparing…"} good={printfulLive} />
          {shipped && <Row label="Tracking" value={`${order.tracking.carrier || ""} · ${order.tracking.tracking_number}`} good />}
          {buyerEmail && <Row label="Confirmation email" value={buyerEmail} good />}
        </div>

        {/* Order summary preview — lists the actual items purchased so the buyer sees exactly what shipped. */}
        {paid && Array.isArray(order?.items) && order.items.length > 0 && (
          <div data-testid="order-summary-preview" className="border-t border-white/10 pt-6 text-left space-y-3">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027]">/ Your order</p>
            <ul className="space-y-2">
              {order.items.map((it, i) => (
                <li key={it.sync_variant_id || i} className="flex items-start justify-between gap-3 border-b border-white/10 pb-2 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{it.product_name || `Item #${it.sync_variant_id}`}</p>
                    {it.variant_name && <p className="text-white/55 text-xs">{it.variant_name}</p>}
                    <p className="text-white/50 text-xs mt-0.5">Qty {it.quantity}</p>
                  </div>
                  {Number(it.item_retail_price) > 0 && (
                    <span className="text-white/85 text-sm font-bold whitespace-nowrap">
                      ${(Number(it.item_retail_price) * Number(it.quantity || 1)).toFixed(2)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <div className="space-y-1 pt-2">
              {order?.item_total != null && <Row label="Subtotal" value={`$${Number(order.item_total).toFixed(2)}`} />}
              {order?.shipping_amount != null && <Row label="Shipping" value={`$${Number(order.shipping_amount).toFixed(2)}`} />}
              {order?.total != null && <Row label="Total charged" value={`$${Number(order.total).toFixed(2)}`} good />}
            </div>
            {order?.recipient && (
              <div className="border-t border-white/10 pt-3 text-white/70 text-xs leading-relaxed">
                <p className="text-white/50 uppercase tracking-[0.25em] text-[9px] font-bold mb-1">/ Shipping to</p>
                <p className="text-white">{order.recipient.name}</p>
                <p>{order.recipient.address1}{order.recipient.address2 ? `, ${order.recipient.address2}` : ""}</p>
                <p>{order.recipient.city}{order.recipient.state_code ? `, ${order.recipient.state_code}` : ""} {order.recipient.zip}</p>
              </div>
            )}
          </div>
        )}
        {shipped ? (
          <a href={order.tracking.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="success-tracking-link" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-3 text-[10px] font-bold uppercase tracking-[0.3em]">
            Track shipment <ArrowUpRight size={12} weight="bold" />
          </a>
        ) : (
          <p className="text-white/75 text-sm leading-relaxed">
            <Package size={14} weight="fill" className="inline text-[#B32027] mr-1.5" />
            Your payment is confirmed and your order has been received. You&rsquo;ll receive tracking by email once your order enters production and ships.
          </p>
        )}
      </div>

      {/* Post-purchase — order is DONE. Give the customer things to do next
          without implying they still owe us a form. */}
      {paid && (
        <div className="max-w-2xl w-full mt-2 border-t border-white/10 pt-8 space-y-5">
          <div className="text-center space-y-2">
            <Heart size={36} weight="fill" className="text-[#B32027] mx-auto" />
            <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tighter leading-[0.95]">
              Your merchandise order is complete.
            </h2>
            {linkedSignup ? (
              <p data-testid="post-purchase-copy" className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
                <span className="text-emerald-400">Your tribute is already on the Legacy Wall.</span>{" "}
                Below are the other ways to stay in the challenge if you haven&rsquo;t already.
              </p>
            ) : (
              <p data-testid="post-purchase-copy" className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
                If you haven&rsquo;t joined the challenge or added your tribute to the Legacy Wall yet, you can do that below &mdash; all optional.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {linkedSignup ? (
              // Existing registration detected — show a "View my entry" CTA
              // instead of asking them to join again (avoids duplicates and
              // prevents assuming they still owe us a form).
              <Link to="/legacy-wall" data-testid="success-view-my-entry" className="inline-flex items-center justify-center gap-2 border-2 border-emerald-500 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
                View My Entry
              </Link>
            ) : (
              <Link to={signupHref} data-testid="success-join-legacy" className="inline-flex items-center justify-center gap-2 border-2 border-[#B32027] bg-[#B32027]/10 hover:bg-[#B32027]/20 text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
                Join the Legacy
              </Link>
            )}
            <Link to="/sweat-for-sacrifice/log" data-testid="success-log-minutes" className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
              Log Minutes
            </Link>
            <Link to="/sweat-for-sacrifice/leaderboard" data-testid="success-view-leaderboard" className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
              View Leaderboard
            </Link>
            <Link to="/legacy-wall" data-testid="success-view-wall" className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
              Legacy Wall
            </Link>
            <Link to="/track" data-testid="success-track-order" className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
              Track My Order
            </Link>
            <Link to="/share" data-testid="success-share-challenge" className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center">
              Share the Challenge
            </Link>
            <Link to="/shop" data-testid="success-shop-more" className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors text-center col-span-2 md:col-span-3">
              Shop More Merch
            </Link>
          </div>
        </div>
      )}
    </main>
  );
}

function Row({ label, value, good }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-white/60 uppercase tracking-[0.2em] text-[10px] font-bold">{label}</span>
      <span className={`font-semibold ${good ? "text-[#B32027]" : "text-white/70"}`}>{value}</span>
    </div>
  );
}
